/**
 * contracts.md §3 — `TransportAdapter`, the only thing that knows the broker exists.
 *
 * D60 reframed the Phase-5 migration to owned transport as remediation for a documented
 * control gap, which makes the probability of migration effectively 1.0 and makes this
 * boundary the difference between a contained change and a diffuse one.
 *
 * **The adapter owns** the broker hostname, the port, the credential fetch from Secrets
 * Manager, the client-ID generation, the `S-` / `C-` topic scheme, the subscribe-before-
 * publish ordering, and the timeouts. **Nothing outside it may name any of them** — none of
 * those strings appears in this package, and CI check 1 fails the build if one appears
 * outside the transport package. The implementation is **P4**'s; this is its shape.
 *
 * This does not weaken D60's risk record: the adapter contains the *knowledge* of the
 * plaintext transport, it does not mitigate it.
 */
const GATE_POSITIONS = new Set([
    'closed',
    'opening',
    'stopped-while-opening',
    'open',
    'closing',
    'stopped-while-closing',
]);
/**
 * The one runtime check for a {@link GatePosition}, shared by the API's readers and the PWA's.
 *
 * It lives here for the same reason the union does: a client that hand-rolled its own list would
 * silently drop a member the day one is added, and "the gate reported something we don't render"
 * is indistinguishable on screen from "the gate reported nothing".
 */
export const isGatePosition = (value) => typeof value === 'string' && GATE_POSITIONS.has(value);
/*
 * ---------------------------------------------------------------------------
 * R4/D225 — the `Centr=` field, and the one question a position is asked.
 * ---------------------------------------------------------------------------
 */
/** The vendor's `Centr=` field, as it appears on the wire. */
const CENTR_FIELD = 'STA:Centr=';
/**
 * `DeviceDto.stateTable`, as the vendor app decodes `Centr=`.
 *
 * **10–15 are the pedestrian-width variants of 0–5**, and they carry the same position: this is the
 * momentary `CMD:OpenP` command's result, never D87's reserved `pedestrian` *operating state*, which
 * is refused at P8's endpoint and stored on no gate record.
 */
const CENTR_CODES = {
    0: 'closed',
    1: 'opening',
    2: 'stopped-while-opening',
    3: 'open',
    4: 'closing',
    5: 'stopped-while-closing',
    10: 'closed',
    11: 'opening',
    12: 'stopped-while-opening',
    13: 'open',
    14: 'closing',
    15: 'stopped-while-closing',
};
/** The position a `Centr=` code names, or `undefined` for a code this firmware table has no row for. */
export const gatePositionOfCentrCode = (code) => CENTR_CODES[code];
/** Whether a `Centr=` code is one of the pedestrian-width variants (10–15). */
export const isPedestrianCentrCode = (code) => code >= 10;
/**
 * The position one frame reports, or `undefined` for every other frame — **the single reader of
 * this field, and the reason the field's own name travels with its table.**
 *
 * D225(i) says the *table* moves here. It arrives with `STA:Centr=` attached because a table without
 * its field name forces every caller to slice the prefix itself, which is four copies of the same
 * three facts (the field is `Centr`, it is `STA:`-prefixed, its value is an integer) — the fork the
 * move was made to prevent, reappearing one layer down. Everything else about a burst is still
 * framing and still lives in `packages/transport`.
 *
 * A non-integer value is `undefined` rather than `0`: **absence is absence** (D46), and a garbled
 * frame must not decode to `closed`, which is the one value this path reads as *nothing happened*.
 */
export function gatePositionOfFrame(frame) {
    const trimmed = frame.trim();
    if (!trimmed.startsWith(CENTR_FIELD))
        return undefined;
    const code = Number(trimmed.slice(CENTR_FIELD.length));
    return Number.isInteger(code) ? gatePositionOfCentrCode(code) : undefined;
}
/**
 * **R4/D225, narrowed by R7/D301 — whether the gate says it is acting TOWARD OPEN, which is the
 * only question a position is asked on the open path.**
 *
 * **The set is `opening` and `open`, and it is the exact mirror of {@link gateIsClosingOrClosed}'s
 * `{closing, closed}`.** It read *every position but `closed`* until D301, and what took the three
 * other members out is the same measurement that had already taken `stopped-while-opening` out of
 * the Close's set:
 *
 *  - **`stopped-while-closing` is the state ONE bare Open produces on a closing leaf.** R7 S1 C3
 *    measured a bare command sent to a moving leaf **stopping** it — it does not reverse and is not
 *    refused — and C12 measured that exact case on the open side: `CMD:OpenN` to a leaf mid-close
 *    answered `Centr=5 stopped-while-closing` at +496 / +532 ms. Counting it as Success tells a
 *    resident the gate opened **while it stands part closed**, which is PEC-51 and is the one
 *    direction this product must never fail in.
 *  - **`closing` is out for the same reason read one beat earlier** — a leaf travelling *away* from
 *    open is not this command's goal met, whoever started it.
 *  - **`stopped-while-opening` is out as the mirror of the Close's refusal of
 *    `stopped-while-closing`.** 🔸 It is written from the mirror rather than measured: C12 produced
 *    `stopped-while-closing`, not this, and an Open that stops an *opening* leaf is the same class
 *    of stranded gate.
 *
 * **The name is deliberately unchanged, and it now reads broader than the set.** `gateIsActing` and
 * `ACTING_POSITIONS` are quoted by name in `AGENTS.md` §4.4, `STATE.md` and `measurements.md` W20,
 * and `gateIsActing(undefined)` is still `false`, so every sentence written about this predicate
 * stays true; renaming it would invalidate a paper trail to gain a word. Read the *question* off
 * this docblock rather than off the identifier.
 *
 * **It rests on what the position MEANS, never on wire behaviour, which is what makes it
 * transport-independent.** C12/C13 are cloud measurements, but nothing above them is about MQTT:
 * a leaf that has stopped mid-close has not opened on any radio. That is why the narrowing is safe
 * for all three consumers below, only one of which was measured.
 *
 * **Three consumers, and the blast radius is stated because two of them inherit this untested:**
 *
 *  - `deliveryOutcome()`'s open arm (`packages/transport/src/command.ts`) — the cloud open path,
 *    which C13 measured.
 *  - `awaitContendedVerdict` (`packages/transport/src/lease.ts`) — D294's contended verdict, ~2% of
 *    opens, read off the gate root's stored `lastState`. It inherits the fix; **C13 did not measure
 *    it**, and a refused caller whose holder left the leaf `stopped-while-closing` now reads
 *    `not-acting` where it read `acting`, which is the same correction one layer up.
 *  - The Web Bluetooth leaf (`web/src/ble/webBluetooth.ts`). It inherits it; **C13 did not measure
 *    it**. On BLE the verdict is usually settled by a real `CMD:Ok` protocol acknowledgement at
 *    ~240 ms before the position branch is reached (`AGENTS.md` §4.9), so this is a fallback path
 *    and the change is in the safe direction: it can only turn a stranded leaf's Success into a
 *    Failed, never the reverse.
 *
 * **It is written as a list rather than as `!== 'closed'`, and that argument survives D301 as the
 * STRONGER half rather than the repeated one.** The negation is shorter, it silently adopts every
 * member a later firmware table adds — a new state would default to *Success* — **and it is now the
 * refuted form**: it is exactly what counted a part-closed gate as an opening. A member added here
 * is a decision, and this is where it has to be made. It is also the shape that reads `undefined` —
 * a burst carrying no position at all — as acting, which would make a gate that answered nothing
 * report Success.
 *
 * 🔸 **It answers about the gate, never about this command.** A gate already travelling on a
 * neighbour's open answers `opening` to a tap that did nothing, and that is deliberate — D225 draws
 * the resident's line at *delivery*, and cost **C5** states the limit: the status read can say *the
 * gate is acting*, never *your command acted*. Nothing may read this for a stronger purpose.
 */
const ACTING_POSITIONS = new Set(['opening', 'open']);
export const gateIsActing = (position) => position !== undefined && ACTING_POSITIONS.has(position);
/**
 * **R4/D233 — whether the leaf is already mid-transition, which is a DIFFERENT question from
 * {@link gateIsActing} and must never be collapsed into it.**
 *
 * `gateIsActing` decides the **verdict**: *has the gate met this command's goal, whoever started
 * it* — so `open` belongs there, a gate standing open having opened. This decides
 * **termination**: *is the gate currently unable to announce a new state change, because it is
 * already in motion*. Only `opening` and `closing` answer yes.
 *
 * **The two sets overlap on `opening` alone and neither contains the other, which is D301's doing
 * and is what makes *narrower* and *broader* the wrong axis here.** Until D301 `gateIsActing` was
 * *every position but `closed`* and did contain this set; it is now `{opening, open}`, so `closing`
 * terminates an exchange without being a Success and `open` is a Success that terminates nothing.
 * A swap in either direction is a different defect, and the clause below is the one measured.
 *
 * **This set may never be replaced by the verdict's, and that is the whole clause.**
 * A command's frame is emitted on physical state change (D225(g)), so a gate that is **idle** —
 * `open`, `closed`, `stopped-while-opening`, `stopped-while-closing` — *will* answer a new command
 * with a frame of its own: measured **+7,457 ms** on a resting-open gate, 2026-09-02. That frame is
 * the stronger answer (D134/D232), so waiting for it is correct. Ending the exchange on
 * `gateIsActing` instead would throw it away on the two idle positions that set still holds
 * (`open`, and every member it held before D301), and would end a **`closed`** exchange at ~600 ms
 * with a position read taken **before the motor started** — which is precisely what D225(k)
 * refused `STA:End` for, arrived at by a different route.
 *
 * The asymmetry is what licenses a one-directional terminator at all: at ~600 ms `closed` is
 * ambiguous — equally *will not move* and *about to* — while nothing about *the leaf is in motion*
 * becomes untrue by waiting longer.
 *
 * 🔸 Like {@link gateIsActing} it answers about the **gate**, never about this command (cost
 * **C5**), which is exactly why it is sound here: the two-resident case is the one D225 was minted
 * to answer.
 */
const IN_MOTION_POSITIONS = new Set(['opening', 'closing']);
export const gateIsInMotion = (position) => position !== undefined && IN_MOTION_POSITIONS.has(position);
/**
 * **R7/D277 (c) — whether the gate is acting toward CLOSED, which is a third question and a
 * SIBLING of {@link gateIsActing} rather than a widening of it.**
 *
 * `gateIsActing` asks *is the leaf doing something, whoever started it* — the open path's only
 * question, and one a Close cannot reuse: a gate reporting `opening` is acting, and it is acting
 * in the wrong direction. {@link gateIsInMotion} is the precedent for a second predicate rather
 * than a parameter on the first, and `gateIsActing`'s own docblock says the two questions must
 * not collapse. This is the third.
 *
 * **The set is `closing` and `closed`, and it is NARROWER than the symmetry D277 (c) proposed.**
 * That clause read *"every position but `open`"*, written from the mirror of `gateIsActing`'s
 * *"every position but `closed`"*, and carried a 🔸 saying S1's rows confirm or refute the cells.
 * **They refuted it**, measured 2026-09-08 on the wire (`measurements.md` §1.1.7):
 *
 *  - **`stopped-while-opening` is the exact state ONE Close produces.** C3 — a bare Close sent to
 *    a moving leaf **stops it**, it does not reverse and is not refused — so the leaf ends stopped
 *    mid-travel, which is **PEC-49's outcome**. The proposed mirror counts that as Success, i.e.
 *    it tells a resident the gate closed **while it stands half open**. That is the one direction
 *    this product must never fail in, so both `stopped-while-*` members are **out**.
 *  - **`closed` is IN, and that cell was confirmed rather than assumed.** C2 — a Close on an
 *    already-closed gate actuates once and is then ignored, answering nothing; without the
 *    appended `STA:*?` the exchange times out and files **Failed on a gate that is correctly
 *    shut**, and with it the burst answers `closed` and the resident is told the truth. 🔸 Its
 *    stated cost: `closed` → Success cannot be told apart from *the gate ignored me because it
 *    thought it was already shut*.
 *  - **`open` is out** — it is the Close's binding sample (D238's rule mirrored) and means the
 *    motor has not started. 🔸 **Unverifiable on the rig this was measured on**, a sensorless
 *    bench controller that will not report `open` for more than a few seconds (`measurements.md`
 *    §3.1 W9/W10); it is the mirror of `gateIsActing`'s `closed` and is written from that.
 *
 * **Written as a list for {@link gateIsActing}'s own reason**, and here the argument is stronger
 * rather than merely repeated: `!== 'open'` is the shorter form *and it is the refuted one*, so a
 * later firmware state must be classified here rather than defaulting into Success.
 *
 * 🔸 Like both siblings it answers about the **gate**, never about this command (cost **C5**).
 */
const CLOSING_POSITIONS = new Set(['closing', 'closed']);
export const gateIsClosingOrClosed = (position) => position !== undefined && CLOSING_POSITIONS.has(position);
export function directionOf(command) {
    switch (command) {
        case 'CMD:CloseN':
        case 'CMD:CloseP':
            return 'close';
        case 'CMD:OpenN':
        case 'CMD:OpenP':
        case 'CMD:Stop':
        case 'STA:*?':
            return 'open';
    }
}
/**
 * **D310 — a bare frame is a command's own answer ONLY when the position it reports is one that
 * command can produce. This is the one predicate the exchange's terminator and the verdict share.**
 *
 * D225(f) told a command's answer from a query's reply **by shape** — a bare, unwrapped single is
 * the controller announcing a physical state change, a delimited `STA:Beg`…`STA:End` burst is the
 * gate answering `STA:*?` — and D289 ended a `send` on that bare frame. Two measurements say shape
 * alone is not enough, because the controller emits bare frames a command did not provoke:
 *
 *  - **The unprompted announcement.** A bare `STA:Centr=` every ~60.3 s on the controller's own
 *    clock (`measurements.md` §1.1.7, §1.1.9), so ≈9.9 % of taps (6,000 / ~60,350) meet one inside
 *    `COMMAND_ACK_TIMEOUT_MS`. On a powered controller whose leaf does not respond that is a false
 *    `acknowledged` — Success, over a gate that reports `closed` and moves nothing (**W15**). On a
 *    healthy open it lands before the motor frame ≈2.6 % of the time: the right verdict by luck,
 *    with `closed` persisted as the position and `OpenAckLatency` taking a heartbeat's latency.
 *  - **The compound's own Stop.** R7 S1 C3/C4 measured a Stop's `Centr=2 stopped-while-opening`
 *    at **+578 / +615 ms**, while the Close's verdict arms at PUBACK (~60 ms) + `STOP_SETTLE_MS`
 *    (500) ≈ **+560 ms** — so the Stop's frame lands 18–55 ms AFTER arming, past D278's structural
 *    guard, and is read as the Close's acknowledgement while the Close's real answer (C5: `closing`
 *    1.7–2.6 s after the Stop) is never heard. If the Close is dropped (C5: probabilistic) the leaf
 *    stands half open under a green tick — PEC-49 with the sentence D277 said it would never carry.
 *
 * **The sets are written as explicit lists, never as negations** — {@link gateIsActing}'s own
 * argument: a `!==` form silently adopts every member a later firmware table adds, and a member
 * added here is a decision made here.
 *
 *  - **Open** (`CMD:OpenN` / `CMD:OpenP`): `opening`, `open`, and **`stopped-while-closing`** — the
 *    last because it is the state one bare Open produces on a closing leaf (C12) and is **D301's
 *    repeat trigger**: it must stay an answer, or the repeat never fires. `closed` is out (the
 *    heartbeat's commonest value, and a pre-motor read on a gate about to open); `closing` and
 *    `stopped-while-opening` are out because no Open produces them.
 *  - **Close** (`CMD:CloseN` / `CMD:CloseP`): `closing` and `closed` — {@link gateIsClosingOrClosed}'s
 *    own set, for the same measured reason: `stopped-while-opening` is what the **Stop** produces
 *    (C3/C4), so it can never be the Close's answer.
 *
 * **A frame naming no position at all keeps D225(f)'s reading and answers `true`.** 🔸 That is the
 * one place this predicate departs from its siblings' *absence is absence*, and it does so because
 * the question differs: they ask about a burst that may carry nothing, this asks about a frame that
 * exists and cannot be tested against the command. No bare frame without `Centr=` has ever been
 * observed on this controller (every heartbeat and every command frame carries it), so the clause
 * has no measured case behind it and is the status quo kept rather than a choice made.
 *
 * **Both readers must call this, and only this.** If the adapter kept settling on a rejected frame
 * while the verdict stopped acknowledging it, a healthy open that met a heartbeat would become a
 * false `not-acting` Failed at ≈2.6 % — worse than the defect. `emqx.ts` reads it per frame to
 * decide whether the exchange ends; `command.ts` reads it over the burst's bare frames to decide
 * whether step 2 fires. **A rejected frame is neither the command's answer nor a reason to stop
 * waiting for one** — it does not end the exchange, does not suppress D289's deferred query, and
 * does not reach `lastState`.
 *
 * 🔸 Like its siblings it answers about the **gate**, never about this command (cost **C5**): a
 * heartbeat reporting `opening` on a gate a neighbour opened still answers an Open, which is D225's
 * absorbed second tap and is deliberate.
 */
const OPEN_ANSWER_POSITIONS = new Set([
    'opening',
    'open',
    'stopped-while-closing',
]);
const CLOSE_ANSWER_POSITIONS = new Set(['closing', 'closed']);
export const bareFrameAnswersCommand = (command, position) => {
    if (position === undefined)
        return true;
    return (directionOf(command) === 'close' ? CLOSE_ANSWER_POSITIONS : OPEN_ANSWER_POSITIONS).has(position);
};
//# sourceMappingURL=transport.js.map